import React, { useState } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { FileType } from '../../services/fileTypeService';

interface UploadFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, description: string, fileTypeId?: number) => Promise<void>;
  isUploading?: boolean;
  fileTypes?: FileType[];
}

export const UploadFileModal: React.FC<UploadFileModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isUploading = false,
  fileTypes = [],
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [fileTypeId, setFileTypeId] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setError(null);
      await onUpload(selectedFile, description, fileTypeId);
      // Reset form
      setSelectedFile(null);
      setDescription('');
      setFileTypeId(undefined);
      // onClose will be called by parent after successful upload
    } catch (err: any) {
      setError(err.message || 'Failed to upload file');
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setDescription('');
    setFileTypeId(undefined);
    setError(null);
    onClose();
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleCancel}
      title="Upload"
      isLoading={isUploading}
    >
      {/* Description Input */}
      <div className="mb-4">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={POPUP_STYLES.input}
          placeholder="Description"
          disabled={isUploading}
        />
      </div>

      {/* File Type Dropdown */}
      {fileTypes.length > 0 && (
        <div className="mb-4">
          <select
            value={fileTypeId || ''}
            onChange={(e) => setFileTypeId(e.target.value ? parseInt(e.target.value) : undefined)}
            className={POPUP_STYLES.input}
            disabled={isUploading}
          >
            <option value="">Select File Type (Optional)</option>
            {fileTypes.map((ft) => (
              <option key={ft.id} value={ft.id}>
                {ft.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* File Input */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="file-upload"
            className="w-full px-4 py-2 rounded-lg border border-[#B4D9D4] bg-[#EBF5F5] flex items-center justify-between cursor-pointer hover:bg-[#D0E7E5]"
          >
            <span className="text-[#366A75] text-sm">
              {selectedFile ? selectedFile.name : 'No file selected'}
            </span>
            <svg className="w-5 h-5 text-[#366A75]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
          </label>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Upload Button */}
      <div className="flex justify-center gap-4">
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className={POPUP_STYLES.button}
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
        <button
          onClick={handleCancel}
          disabled={isUploading}
          className={POPUP_STYLES.button}
        >
          Cancel
        </button>
      </div>
    </ModalWrapper>
  );
};
